import { type ElementType } from 'react';
import { Card, type CardShadow } from '../Card';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { Badge, type BadgeState } from '../Badge';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * CampaignCard component props.
 *
 * Sealed — `className` is not exposed. CampaignCard is a fixed-layout design
 * system component. If a new visual variant is needed, add a prop here rather
 * than reaching for a className override.
 */
export interface CampaignCardProps {
  /** URL of the product image. Displayed full-width at the top of the card. */
  imageUrl?: string;
  /** Alt text for the product image. Defaults to an empty string. */
  imageAlt?: string;
  /** Product display name. Rendered in body/large/bold as h3. */
  productName: string;
  /** Optional badge states displayed below the product name. */
  tags?: ReadonlyArray<BadgeState>;
  /** Optional status badge displayed over the product image. */
  badgeState?: BadgeState;
  /** Optional number used by numbered badge states. */
  badgeNumber?: number;
  /** Optional localized or context-specific status label override. */
  badgeLabel?: string;
  /** CTA button label. Defaults to "View program". */
  ctaLabel?: string;
  /** CTA button variant. Defaults to "primary". */
  ctaVariant?: 'primary' | 'secondary';
  /** When true, the CTA button is disabled (e.g. expired campaigns). */
  ctaDisabled?: boolean;
  /** Called when the user taps the CTA button. */
  onViewProposal: () => void;
  /**
   * Shadow depth forwarded to the inner Card.
   * @see CardShadow
   */
  shadow?: CardShadow;
  /** Root HTML element for the inner Card. @default "div" */
  as?: ElementType;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer CampaignCard component.
 *
 * A vertical-layout card presenting a campaign/product opportunity. The product
 * image spans the full card width at the top; the content pane below holds the
 * brand label, product name, optional tags, and a "View program" CTA button.
 *
 * ### Layout
 * - **Top** — square aspect ratio (`aspect-square`), `object-cover` on `bg-surface` background.
 *   Scales proportionally with the card/column width. No fixed dimensions — works at any grid size.
 * - **Bottom** — `p-4 flex flex-col gap-3`; product name (h3), tags row, button
 *
 * ### Sealed component
 * `className` is not exposed. Layout (width, margin, grid placement) belongs on
 * the parent container. If a new visual state is needed, add a prop.
 *
 * @example
 * <CampaignCard
 *   imageUrl="/images/nespresso.png"
 *   productName="Nespresso Vertuo Pop Coffee Machine"
 *   tags={[BadgeState.DELIVERED, BadgeState.REVIEW_SOCIAL]}
 *   onViewProposal={() => navigate({ to: '/campaign/$id', params: { id: '1' } })}
 * />
 */
export function CampaignCard({
  imageUrl,
  imageAlt = '',
  productName,
  tags,
  badgeNumber,
  badgeState,
  badgeLabel,
  ctaLabel = 'View program',
  ctaVariant = 'primary',
  ctaDisabled = false,
  onViewProposal,
  shadow,
  as,
}: CampaignCardProps) {
  return (
    <Card shadow={shadow} as={as}>
      {/* ── Product image — square aspect ratio, cropped to fill, scales with card width ── */}
      {imageUrl && (
        <div className="w-full aspect-square bg-surface overflow-hidden relative">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/assets/images/placeholder.svg';
            }}
          />
          {badgeState && (
            <div className="absolute top-2 right-2">
              <Badge state={badgeState} number={badgeNumber} testId="campaign-badge">
                {badgeLabel}
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* ── Divider between image and content ─────────────────────────── */}
      {imageUrl && <Divider />}

      {/* ── Content pane ──────────────────────────────────────────────────── */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          {/* Product name */}
          <Typography variant="header" size="h1" as="h1">
            {productName}
          </Typography>
        </div>

        {/* Tags + CTA — mt-auto keeps both pinned to the bottom of the card */}
        <div className="mt-auto flex flex-col gap-3">
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map((state) => (
                <Badge key={state} state={state} />
              ))}
            </div>
          )}

          <Button fullWidth variant={ctaVariant} onClick={onViewProposal} disabled={ctaDisabled}>
            {ctaLabel}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default CampaignCard;
